// Page: ManageWebsites
// Purpose: Admin list and filters for websites. Supports search, tag filtering, sorting, pagination, edit, and delete.
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import { ConfirmationModal } from '../../../components/ui/modals/confirmation-modal.jsx';
import Footer from '../../../components/shared/Footer';
import { Globe, Search, Filter, ArrowUpDown, ChevronDown, ArrowLeft, Edit, Trash2, ExternalLink } from 'lucide-react';
import { WEBSITE_TAG_LIST } from '../../../data/tags/tagEnums';
import { getAllWebsitesForAdmin, deleteWebsite } from '../../../api/websites/websitesApi';
import { isUserAdmin } from '../../../utils/admin/adminUtils';

export default function ManageWebsites() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);

  const isAdmin = isUserAdmin(user);

  const [websites, setWebsites] = useState([]);
  const [filteredWebsites, setFilteredWebsites] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingWebsites, setLoadingWebsites] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    websiteId: null,
    title: '',
    isDeleting: false
  });

  const sortDropdownRef = useRef(null);

  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      }
      setIsLoading(false);
    } else if (user === null) {
      navigate('/login');
    }
  }, [navigate, user, isAdmin]);

  const loadWebsites = async (page = 1, resetList = false) => {
    if (loadingWebsites) return;
    
    setLoadingWebsites(true);
    
    try {
      const options = {
        page,
        limit: 25,
        search: searchQuery,
        tags: selectedTags.join(','),
        sortBy
      };

      const response = await getAllWebsitesForAdmin(options);
      
      if (response) {
        const newWebsites = resetList ? response.websites : [...websites, ...response.websites];
        
        setWebsites(newWebsites);
        setFilteredWebsites(newWebsites);
        setTotalCount(response.totalCount || 0);
        setCurrentPage(response.currentPage || page);
        setTotalPages(response.totalPages || 1);
        setHasMore(response.hasMore || false);
      }
    } catch (error) {
      console.error('Error loading websites:', error);
      addToast('Failed to load websites. Please try again.', 'error');
    } finally {
      setLoadingWebsites(false);
    }
  };

  useEffect(() => {
    if (!isLoading && isAdmin) {
      loadWebsites(1, true);
    }
  }, [isLoading, isAdmin, searchQuery, selectedTags, sortBy]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => {
      const newTags = prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag];
      setCurrentPage(1);
      return newTags;
    });
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    setCurrentPage(1);
    setShowSortDropdown(false);
  };

  const handleLoadMore = () => {
    if (hasMore && !loadingWebsites) {
      loadWebsites(currentPage + 1, false);
    }
  };

  const handleDeleteWebsite = (websiteId, title) => {
    setDeleteModal({
      isOpen: true,
      websiteId,
      title,
      isDeleting: false
    });
  };

  const confirmDelete = async () => {
    if (!deleteModal.websiteId) return;

    setDeleteModal(prev => ({ ...prev, isDeleting: true }));

    try {
      await deleteWebsite(deleteModal.websiteId);
      
      addToast('Website deleted successfully.', 'success');

      setDeleteModal({ isOpen: false, websiteId: null, title: '', isDeleting: false });
      loadWebsites(1, true);
    } catch (error) {
      console.error('Error deleting website:', error);
      addToast('Failed to delete website. Please try again.', 'error');
      setDeleteModal(prev => ({ ...prev, isDeleting: false }));
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, websiteId: null, title: '', isDeleting: false });
  };

  const handleEditWebsite = (websiteId) => {
    navigate(`/app/private/editWebsite/${websiteId}`);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sortDropdownRef]);

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  const sortOptions = [
    { value: 'newest', label: 'Newest first' },
    { value: 'oldest', label: 'Oldest first' },
    { value: 'title-asc', label: 'Title A-Z' },
    { value: 'title-desc', label: 'Title Z-A' }
  ];

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto pb-20">
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/admin/templates')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Templates
        </button>
      </div>

      <div className="mb-6 flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Manage Websites</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            View, edit, and manage all websites
          </p>
        </div>
        <button
          onClick={() => navigate('/app/private/createWebsite')}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
        >
          <Globe className="h-4 w-4" />
          Create Website
        </button>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="bg-white dark:bg-gray-900 overflow-hidden shadow rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-5">
            <div className="flex items-center">
              <Globe className="h-8 w-8 text-gray-400" />
              <div className="ml-5">
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Websites</dt>
                <dd className="text-lg font-medium text-gray-900 dark:text-white">{totalCount}</dd>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search websites..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
            />
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${
                showFilters || selectedTags.length > 0
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filters
              {selectedTags.length > 0 && <span className="bg-white bg-opacity-20 text-xs px-1.5 py-0.5 rounded-full">{selectedTags.length}</span>}
            </button>

            <div className="relative" ref={sortDropdownRef}>
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <ArrowUpDown className="h-4 w-4" />
                Sort
                <ChevronDown className="h-4 w-4" />
              </button>
              
              {showSortDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                  <div className="py-1">
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleSortChange(option.value)}
                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                          sortBy === option.value ? 'bg-primary-50 text-primary-600 dark:bg-primary-900 dark:text-primary-300' : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {showFilters && (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {WEBSITE_TAG_LIST.map((tag) => (
                <button
                  key={tag}
                  onClick={() => handleTagToggle(tag)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md border ${
                    selectedTags.includes(tag)
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
            {selectedTags.length > 0 && (
              <div className="flex justify-end mt-2">
                <button
                  onClick={() => setSelectedTags([])}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm">
        {loadingWebsites && currentPage === 1 ? (
          <div className="flex items-center justify-center p-12">
            <LoadingSpinner message="Loading websites..." />
          </div>
        ) : filteredWebsites.length === 0 ? (
          <div className="text-center py-12">
            <Globe className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No websites found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchQuery || selectedTags.length > 0 ? 'Try adjusting your search or filters.' : 'Get started by creating your first website.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredWebsites.map((website) => (
              <div
                key={website.id}
                className="bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:bg-white/5 dark:hover:bg-gray-800/20 transition-all"
              >
                <div className="flex items-start justify-between">
                  {/* Left side - Thumbnail and content */}
                  <div className="flex items-start space-x-4 flex-1 min-w-0">
                    {/* Thumbnail */}
                    <div className="flex-shrink-0">
                      {website.thumbnailUrl ? (
                        <a
                          href={website.websiteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="relative group block"
                        >
                          <img
                            src={website.thumbnailUrl}
                            alt={website.title}
                            className="h-20 w-32 object-cover rounded-lg border border-gray-200 dark:border-gray-600 transition-opacity group-hover:opacity-75"
                          />
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="bg-black/60 rounded-full p-2">
                              <ExternalLink className="h-5 w-5 text-white" />
                            </div>
                          </div>
                        </a>
                      ) : (
                        <a
                          href={website.websiteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-20 w-32 bg-gray-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                          <Globe className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                        </a>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <Globe className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                          {website.title}
                        </h3>
                      </div>
                      
                      <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                        <span>Created: {new Date(website.createdAt).toLocaleDateString()}</span>
                        {website.websiteUrl && (
                          <>
                            <span>•</span>
                            <a
                              href={website.websiteUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              View Website
                            </a>
                          </>
                        )}
                      </div>

                      {website.tags && website.tags.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {website.tags.slice(0, 5).map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                            >
                              {tag}
                            </span>
                          ))}
                          {website.tags.length > 5 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                              +{website.tags.length - 5} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right side - Action buttons */}
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleEditWebsite(website.id)}
                      className="inline-flex items-center p-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      title="Edit Website"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    
                    <button
                      onClick={() => handleDeleteWebsite(website.id, website.title)}
                      className="inline-flex items-center p-2 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 bg-white dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      title="Delete Website"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Load More Button */}
            {hasMore && (
              <div className="flex justify-center pt-6">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingWebsites}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {loadingWebsites ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Loading...</span>
                    </>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Website"
        message={`Are you sure you want to delete "${deleteModal.title}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        isLoading={deleteModal.isDeleting}
      />

      <Footer />
    </div>
  );
}

